<template>
  <div class="import-changes" :class="{ 'import-changes--full': full }">
    <ul v-if="entries.length" class="import-changes__list">
      <li
        v-for="entry in shown"
        :key="entry.key"
        class="import-changes__item"
        :aria-label="`${entry.label}: ${entry.oldText} changes to ${entry.newText}`"
      >
        <span class="import-changes__field">{{ entry.label }}</span>
        <span class="import-changes__values">
          <span
            v-tooltip="tooltip(entry.oldText)"
            class="import-changes__old"
          >{{ entry.oldText }}</span>
          <span class="import-changes__arrow" aria-hidden="true">→</span>
          <span
            v-tooltip="tooltip(entry.newText)"
            class="import-changes__new"
          >{{ entry.newText }}</span>
        </span>
      </li>
    </ul>
    <span v-else class="import-changes__empty">No changes</span>
    <button
      v-if="entries.length > limit"
      type="button"
      class="import-changes__toggle"
      :aria-expanded="expanded ? 'true' : 'false'"
      @click.stop="expanded = !expanded"
    >
      {{ expanded ? 'Show fewer' : `Show ${entries.length - limit} more` }}
    </button>
  </div>
</template>

<script>
import { formatChangeValue } from '../importState'

const LONG_TEXT = 36
const CODE_KEYS = /^(gender|positions|language|language_level|ec\d_relationship)$/

export default {
  name: 'CrewImportChanges',
  props: {
    changes: { type: Object, default: () => ({}) },
    labels: { type: Object, default: () => ({}) },
    limit: { type: Number, default: 3 },
    // the whole value is shown (wrapped) instead of one line cut with an ellipsis
    full: { type: Boolean, default: false }
  },
  data() {
    return { expanded: false }
  },
  computed: {
    entries() {
      // Follow the order of the registry (labels), not the order of the file columns.
      const order = Object.keys(this.labels)
      const position = key => (order.includes(key) ? order.indexOf(key) : order.length)
      return Object.entries(this.changes || {})
        .sort(([first], [second]) => position(first) - position(second))
        .map(([key, change]) => ({
          key,
          label: this.labels[key] || key,
          oldText: this.text(key, change.old),
          newText: this.text(key, change.new)
        }))
    },
    shown() {
      return this.expanded ? this.entries : this.entries.slice(0, this.limit)
    }
  },
  methods: {
    // Dictionary values travel as codes (chief_guide); show them as words.
    text(key, value) {
      if (!CODE_KEYS.test(key)) return formatChangeValue(value)
      const words = formatChangeValue(value).replace(/_/g, ' ')
      return words.charAt(0).toUpperCase() + words.slice(1)
    },
    tooltip(text) {
      if (text.length > LONG_TEXT) return { content: text, placement: 'top', offset: 8, classes: 'tooltip--new' }
      return { content: '', classes: 'tooltip--new' }
    }
  }
}
</script>

<style lang="scss" scoped>
.import-changes {
  min-width: 0;
  font-size: $ui-font-size-body;

  &__list {
    display: flex;
    flex-direction: column;
    gap: $ui-space-4;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__item {
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr);
    gap: $ui-space-8;
    align-items: baseline;
  }

  &__field { color: $ui-muted; font-size: $ui-font-size-metadata; font-weight: 600; }

  &__values {
    display: flex;
    align-items: baseline;
    gap: $ui-space-8;
    min-width: 0;
  }

  &__old,
  &__new {
    display: inline-block;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &--full &__old,
  &--full &__new {
    max-width: none;
    overflow: visible;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  &__old { color: $ui-muted; text-decoration: line-through; }
  &__new { color: $ui-ink; font-weight: 600; }
  &__arrow { color: $ui-subtle; }
  &__empty { color: $ui-muted; }

  &__toggle {
    margin-top: $ui-space-8;
    padding: 0;
    border: 0;
    background: none;
    color: $ui-blue;
    font-family: $ui-font-body;
    font-size: $ui-font-size-control;
    font-weight: 600;
    cursor: pointer;

    &:hover { color: $ui-blue-hover; text-decoration: underline; }
    &:focus-visible { outline: 2px solid $ui-blue; outline-offset: 2px; }
  }
}

@media (max-width: 760px) {
  .import-changes__item { grid-template-columns: minmax(0, 1fr); gap: 0; }
}
</style>
